import { SERVICE_PAGES as LEGACY_PAGES } from './servicePages.js';
import { SERVICES, SERVICE_GROUPS } from './seoPages.js';
import { SERVICE_LANDINGS } from './serviceLandings.js';
import { mediaSrc } from '../lib/media.js';
import { IMAGES } from './site.js';

// Preserve imported content, but use the approved catalog for all public names.
// Old addresses remain supported and redirect to the corresponding service.
export const SERVICE_ALIASES = {
  additions: 'home-additions',
  'full-home-renovation-service-2': 'home-renovations',
  'kitchen-renovation-services': 'kitchen-renovations',
  'new-home-construction-services': 'new-home-construction',
  'secondary-suites-legal-basement-rental-suites': 'secondary-suites',
  'side-entries-separate-entries-2': 'secondary-suites',
  'side-entries-separate-entries': 'side-entries',
  'concrete-floor-services': 'concrete-flatwork',
  'concrete-services': 'concrete',
  'epoxy-services': 'epoxy-flooring',
  'tree-cutting-services-page': 'tree-cutting',
  'electrical-service': 'electrical',
  'framing-services': 'framing',
  'gas-services-page': 'gas-fitting',
  'kerdi-kit-shower-waterproofing-services': 'kerdi-shower-waterproofing',
  'plumbing-services': 'plumbing',
  'roofing-services': 'roofing',
  'structural-services': 'structural-renovations',
  'stucco-services': 'stucco',
  'flooring-services-page': 'flooring',
  'tiling-services': 'tiling',
  'demolition-service': 'demolition',
  'dex-and-pergola-services': 'decks-fences',
};

export const SERVICE_PAGES = LEGACY_PAGES.map(page => {
  const slug = SERVICE_ALIASES[page.slug] || page.slug;
  const service = SERVICES.find(s => s.slug === slug);
  const name = service?.name || page.name;
  // Landing copy, curated photos (with captions and alt text) and videos for
  // each service live in serviceLandings.js. Photo paths are old-site uploads,
  // served locally as WebP (see lib/media.js).
  const landing = SERVICE_LANDINGS[slug];
  const photos = landing?.photos || page.gallery.map(src => ({ src }));
  return {
    ...page, slug, name, group: service?.group || 'Construction',
    intro: slug === 'roofing' ? service.intro : page.intro,
    hero: mediaSrc(landing?.hero || photos[0]?.src || page.hero),
    gallery: photos.map(p => p.src),
    photos, landing, videos: landing?.videos || [],
    blocks: slug === 'roofing' ? [
      { type: 'h', level: 2, text: 'Roofing' },
      { type: 'p', text: service.intro },
      { type: 'list', items: service.points },
      { type: 'p', text: 'These services cover complete residential roofing projects. Small repairs are outside this offering. Contact us to discuss your property, timing and a project-specific quote.' },
    ] : page.blocks.map(block => block.type === 'h' ? {
      ...block,
      // Imported page headings often repeat a different version of the name.
      text: /^(?:MRE Developments: Expert )?(?:Bathroom Renovation|Kitchen Renovation|Flooring|Gas|Tree Cutting|Painting|Demolition|Roofing|Electrical|Framing|Tiling|Stucco|Additions)(?: Installation)? Services?(?: in Calgary)?$/i.test(block.text)
        ? name : block.text,
    } : block),
  };
}).filter((page, index, pages) => pages.findIndex(p => p.slug === page.slug) === index);

// Catalog services that had no WordPress page get the same landing layout,
// built from their landing copy (or the catalog entry until copy exists).
for (const service of SERVICES) {
  if (SERVICE_PAGES.some(page => page.slug === service.slug)) continue;
  const landing = SERVICE_LANDINGS[service.slug];
  const photos = landing?.photos || [];
  SERVICE_PAGES.push({
    slug: service.slug, name: service.name, group: service.group, intro: service.intro,
    hero: landing?.hero || photos[0]?.src ? mediaSrc(landing?.hero || photos[0].src) : undefined,
    gallery: photos.map(p => p.src),
    photos, landing, videos: landing?.videos || [],
    blocks: [{ type: 'list', items: service.points }],
  });
}

// Gallery photos grouped by project (same job, before → after), from serviceLandings.js.
for (const page of SERVICE_PAGES) {
  if (!page.landing?.projects?.length) continue;
  const bySrc = new Map(page.photos.map(p => [p.src, p]));
  page.projects = page.landing.projects
    .map(p => ({ ...p, photos: p.photos.map(src => bySrc.get(src)).filter(Boolean) }))
    .filter(p => p.photos.length);
}

// Every service page keeps the same layout. Services with few or no photos of
// their own borrow real MRE photos from the closest related service: for the
// hero and any feature row without an image, and (when they have none) as a
// "related work" gallery that links back to that service.
const RELATED_PHOTOS = {
  'garages-shops': 'concrete-flatwork', drywall: 'basement-development', soundproofing: 'basement-development',
  insulation: 'framing', 'rtm-homes': 'new-home-construction', 'design-drafting-engineering': 'structural-renovations',
  'tenant-improvements': 'interior-finishings', 'office-fitouts': 'interior-finishings',
  'restaurant-buildouts': 'interior-finishings', 'retail-buildouts': 'interior-finishings',
  'stainless-steel': 'kitchen-renovations', 'commercial-shrink-wrap': 'new-home-construction', solar: 'roofing',
  'gas-fitting': 'plumbing', 'concrete-repairs-and-maintenance': 'concrete', 'commercial-construction': 'interior-finishings',
  waterproofing: 'basement-development', hvac: 'plumbing', 'custom-homes': 'new-home-construction',
};
// Digital & AI pages have no project photography; feature rows use the site's digital imagery.
const DIGITAL_FEATURE_IMAGES = [IMAGES.webdesign, IMAGES.ai, IMAGES.brandwork, IMAGES.growPanel, IMAGES.web2, IMAGES.team];

for (const page of SERVICE_PAGES) {
  if (!page.landing) continue;
  const source = SERVICE_PAGES.find(p => p.slug === RELATED_PHOTOS[page.slug]);
  const isDigital = page.group === 'Digital & AI';
  const used = new Set([page.landing.hero, ...page.landing.features.map(f => f.image)].filter(Boolean));
  // Pages sharing a source start at different photos so they don't look identical.
  const shared = Object.keys(RELATED_PHOTOS).filter(s => RELATED_PHOTOS[s] === source?.slug).indexOf(page.slug);
  const borrowed = source?.photos || [];
  const offset = borrowed.length ? (Math.max(shared, 0) * 2) % borrowed.length : 0;
  const spare = [...page.photos, ...borrowed.slice(offset), ...borrowed.slice(0, offset)].filter(p => !used.has(p.src));
  const take = () => { const p = spare.shift(); if (p) used.add(p.src); return p; };
  if (source && !page.photos.length) {
    const hero = take();
    if (hero) page.hero = mediaSrc(hero.src);
    page.heroAlt = hero?.alt;
    page.related = { slug: source.slug, name: source.name, photos: source.photos, projects: source.projects };
  }
  let digitalIndex = SERVICE_PAGES.indexOf(page);
  page.features = page.landing.features.map(f => {
    if (f.image) return f;
    if (isDigital) return { ...f, image: DIGITAL_FEATURE_IMAGES[digitalIndex++ % DIGITAL_FEATURE_IMAGES.length], imageAlt: '' };
    const p = take();
    return p ? { ...f, image: p.src, imageAlt: p.alt } : f;
  });
}

export const getServicePage = slug => SERVICE_PAGES.find(page => page.slug === slug);
export const SERVICE_CATALOG = SERVICES.map(service => {
  const page = getServicePage(service.slug);
  return { ...service, hero: page?.hero, intro: service.intro || page?.intro };
});
export { SERVICE_GROUPS };
